import "./TaskPanelLeft.css";

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { ArrowRoundRightLightIcon, EditPencilIcon, PlusIcon, RepeatIcon } from "../../../../assets";
import { OrganisationTypeConstants, WorkflowOrganisationAccessLevel } from "../../../../constants";
import { UploadDealDocumentModal } from "../../../../route/asset-manager/deal/components/UploadDealDocumentModal";
import { UploadDocumentModal } from "../../../../route/shared/projects/project/documents/components/UploadDocumentModal";
import { hasWorkflowOrganisationAccessLevel } from "../../../../utils";
import { getWorkflowTasksBoardRoute } from "../../../../utils/routes";
import {
  Autocomplete,
  Button,
  Column,
  DiscussionsPanel,
  Divider,
  ErrorBox,
  FormLabel,
  KanaTooltip,
  Row,
  Tag,
  TaskTags,
  TextArea,
  TextInput,
  Toast,
} from "../../../../widget";
import { useTaskPanelContext } from "../../TaskPanelContext";
import { useTaskDiscussions } from "../../useTaskDiscussions";
import { RemoveAttachmentModal } from "../RemoveAttachmentModal";
import { TaskPanelAttachments } from "../TaskPanelAttachments";

export const TaskPanelLeft = (): JSX.Element => {
  const {
    errors,
    hasFullEditAccess,
    workflow,
    currentUserType,
    workflowOrganisationAccessLevel,

    activitySelectData,

    isCreateTask,
    isGeneralTask,
    parentName,
    parentObjectType,

    isTitleEditable,
    setIsTitleEditable,
    currentTask,
    setCurrentTask,

    linkedTags,
    availableTags,
    setNewTagName,
    handleToggleTag,
    handleRemoveTag,
    handleAddNewTag,

    attachments,
    isAttachmentsLoading,
    canAddAttachment,
    canRemoveAttachment,
    attachmentParentType,
    attachmentParentUuid,
    beginUpload,
    handleDocumentsUploaded,
    handleRemoveAttachment,
    handleDownloadAttachment,

    handleLinkedActivityOnClick,
    handleSubmit,
    handleCloseTaskPanel,
    isHandleSubmitLoading,
  } = useTaskPanelContext();

  const {
    threads,
    permissions,
    currentUserUuid,
    hasMoreFromServer,
    hasMoreResolvedFromServer,
    onShowMore,
    onShowMoreResolved,
    onShowResolvedChange,
    onAddComment,
    onReply,
    onEditThreadMessage,
    onEditReply,
    onDeleteThread,
    onDeleteReply,
    onResolveThread,
    errors: discussionErrors,
  } = useTaskDiscussions(currentTask.uuid ?? undefined);

  // The add-tag input is revealed by the "+" chip, matching the design.
  const [showAddTag, setShowAddTag] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  /*
    The panel session the open upload belongs to, fixed before the file transfers. Held as state rather than a
    ref so the modal's onUploaded closes over the value: an upload that finishes after this task was closed
    then still reports the session it was started in, whether or not this component was remounted since.
  */
  const [uploadToken, setUploadToken] = useState(0);
  /*
    The project upload modal requires this pair, which it flips to tell a documents table to reload. The
    overlay has no such table — it reloads from the uploaded document reported to onUploaded — so the flag
    is held here purely to satisfy the modal's contract.
  */
  const [refreshUploadedDocuments, setRefreshUploadedDocuments] = useState(false);
  /*
    The attachment the remove prompt is open for. Held rather than a boolean so the prompt can name the file,
    and cleared on confirm or cancel, which is what closes it.
  */
  const [attachmentToRemove, setAttachmentToRemove] = useState<string>();
  const [isRemovingAttachment, setIsRemovingAttachment] = useState(false);

  const availableTagsSelectData = useMemo(
    () => availableTags.map((tag) => ({ key: tag.uuid, value: tag.name })),
    [availableTags]
  );

  // The parent line is a plain name for general tasks and a link to the board for workflow
  // tasks; on create there is nothing to show, so the row is skipped rather than left empty.
  const showParent = isGeneralTask ? Boolean(parentName) : Boolean(workflow);
  const showRepeatFlag = !hasFullEditAccess && Boolean(currentTask.repeat);

  let titleSpacing: "s" | "ml" | "ll" = "s";
  if (!showParent) {
    titleSpacing = "ll";
  } else if (isCreateTask || isTitleEditable) {
    titleSpacing = "ml";
  }

  return (
    <div className="TaskPanelLeft">
      {errors && errors.length > 0 && (
        <Row spacingV="ml">
          <Column span={12}>
            <ErrorBox text={errors.map((error) => error.message)} />
          </Column>
        </Row>
      )}
      <Row spacingV="s">
        <Column span={12}>
          <TaskTags
            isMandatory={currentTask.isMandatory ?? false}
            isMilestone={currentTask.isMilestone ?? false}
            taskType={isGeneralTask ? "general" : "workflow"}
            objectType={parentObjectType}
          />
        </Column>
      </Row>
      <Row spacingV={titleSpacing}>
        <Column span={12}>
          {isCreateTask || isTitleEditable ? (
            <TextInput
              name="Editable Title"
              label="Name*"
              placeholder="Add task name"
              value={currentTask?.name}
              onChange={(value) => setCurrentTask({ ...currentTask, name: value })}
              maxLength={100}
              displayCharacterCount
            />
          ) : (
            <div className="TaskPanelTitle">
              <h1>{currentTask?.name}</h1>
              {hasFullEditAccess && (
                <Button
                  text=""
                  icon={<EditPencilIcon />}
                  variant="secondary"
                  onClick={() => setIsTitleEditable(true)}
                  minWidth={false}
                  hasNoPadding
                />
              )}
            </div>
          )}
        </Column>
      </Row>
      {showParent && (
        <Row spacingV={showRepeatFlag ? "m" : "ll"}>
          <Column span={12}>
            <p className="body1 TaskPanelSubheader">
              {isGeneralTask ? (
                <span>{parentName}</span>
              ) : (
                workflow && <Link to={getWorkflowTasksBoardRoute(workflow.uuid, currentUserType)}>{workflow.name}</Link>
              )}
            </p>
          </Column>
        </Row>
      )}
      {/* Mandatory / milestone are chips above the title; repeat has no chip, so it keeps its icon. */}
      {showRepeatFlag && (
        <Row spacingV="ll">
          <Column span={12}>
            <div className="TaskPanelFlags">
              <KanaTooltip tooltipHeader="" tooltipText="Repeat task" icon={<RepeatIcon className="RepeatIcon" />} />
            </div>
          </Column>
        </Row>
      )}
      {currentTask.activityUuid && activitySelectData.length > 0 && currentTask.relateToActivity && (
        <Row spacingV="ll">
          <Column span={12}>
            <div className="TaskPanelLinkedActivity">
              <FormLabel label="Linked activity" />
              <Button
                text={activitySelectData.find((activity) => activity.key === currentTask.activityUuid)?.value as string}
                onClick={handleLinkedActivityOnClick}
                icon={<ArrowRoundRightLightIcon />}
                hasNoPadding
                corners="rounded"
                variant="forestGreen"
                size="small"
              />
            </div>
          </Column>
        </Row>
      )}
      {(linkedTags.length > 0 || hasFullEditAccess) && (
        <Row spacingV="xl">
          <Column span={12}>
            <div className="TaskPanelTags">
              <FormLabel label="Tags" />
              <div className="TaskPanelTagsRow">
                {linkedTags.map((tag) => (
                  <Tag
                    key={tag.uuid}
                    label={tag.name}
                    hasCloseIcon={hasFullEditAccess}
                    onClose={() => handleRemoveTag(tag.uuid)}
                  />
                ))}
                {hasFullEditAccess && !showAddTag && (
                  <Button
                    text=""
                    icon={<PlusIcon className="TaskPanelTagsAddIcon" />}
                    variant="secondary"
                    /* "custom" opts out of the sized variants' 56px min-height. */
                    size="custom"
                    onClick={() => setShowAddTag(true)}
                    minWidth={false}
                    hasNoPadding
                    className="TaskPanelTagsAddButton"
                  />
                )}
              </div>
              {hasFullEditAccess && showAddTag && (
                <div className="TaskPanelTagsAdd">
                  {/*
                    Type-and-search over the organisation's tags: picking a match links it, while
                    Add creates a tag for a name that does not exist against the organisation yet.
                  */}
                  <Autocomplete
                    /*
                      The combobox owns its text, so remount it once a tag is linked to clear the
                      box — "Add" has no other way to reset it.
                    */
                    key={linkedTags.length}
                    placeholder="Search tags"
                    data={availableTagsSelectData}
                    onSelect={(key) => {
                      // The combobox reports an empty key while the text is being edited.
                      if (!key) return;
                      handleToggleTag(key);
                      setNewTagName("");
                    }}
                    onChange={(value) => setNewTagName(value)}
                    clearOnSelect
                  />
                  <Button text="Add" variant="secondary" size="small" hasNoPadding onClick={handleAddNewTag} />
                </div>
              )}
            </div>
          </Column>
        </Row>
      )}
      {/*
        Attachments are only shown once there is something to show or something the user can do: a task whose
        parent cannot hold documents has no attachments and no way to add any.
      */}
      {(attachments.length > 0 || canAddAttachment) && (
        <Row spacingV="xl">
          <Column span={12}>
            <TaskPanelAttachments
              attachments={attachments}
              isLoading={isAttachmentsLoading}
              canRemove={canRemoveAttachment}
              canAdd={canAddAttachment}
              variant={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "seed" : "hub"}
              onAddClick={() => {
                setUploadToken(beginUpload());
                setShowUploadModal(true);
              }}
              // Confirmed first: the trash icon reads as a delete, and the prompt says what it does not delete.
              onRemove={(documentUuid) => setAttachmentToRemove(documentUuid)}
              onDownload={(documentUuid) => {
                handleDownloadAttachment(documentUuid).catch(() =>
                  Toast.error({ message: "This attachment could not be downloaded" })
                );
              }}
            />
          </Column>
        </Row>
      )}
      <RemoveAttachmentModal
        show={attachmentToRemove !== undefined}
        fileName={attachments.find((a) => a.documentUuid === attachmentToRemove)?.fileName}
        parentKind={attachmentParentType}
        isLoading={isRemovingAttachment}
        closeModal={() => setAttachmentToRemove(undefined)}
        onConfirm={() => {
          if (!attachmentToRemove) return;

          setIsRemovingAttachment(true);
          handleRemoveAttachment(attachmentToRemove)
            .catch(() => Toast.error({ message: "This attachment could not be removed" }))
            /*
              Closed however it ended: the removal reports its own outcome, and leaving the prompt open on a
              failure invites a second unlink of a link that may already be gone.
            */
            .finally(() => {
              setIsRemovingAttachment(false);
              setAttachmentToRemove(undefined);
            });
        }}
      />
      {/*
        Parent-aware upload: a deal task uploads a deal document, a project task a project document, and a
        landholding task a group document, matching the pairings the link accepts. Each modal reports the
        document it created, which is then linked to the task (or staged, when the task is not saved yet).
      */}
      {showUploadModal && attachmentParentUuid && attachmentParentType === "deal" && (
        <UploadDealDocumentModal
          show={showUploadModal}
          closeModal={() => setShowUploadModal(false)}
          dealUuid={attachmentParentUuid}
          onUploaded={(documents) => {
            handleDocumentsUploaded(documents, uploadToken).catch(() =>
              Toast.error({ message: "The document was uploaded, but could not be attached" })
            );
          }}
        />
      )}
      {showUploadModal && attachmentParentUuid && attachmentParentType === "project" && (
        <UploadDocumentModal
          show={showUploadModal}
          closeModal={() => setShowUploadModal(false)}
          projectUuid={attachmentParentUuid}
          refreshDocuments={refreshUploadedDocuments}
          setRefreshDocuments={setRefreshUploadedDocuments}
          onUploaded={(documents) => {
            handleDocumentsUploaded(documents, uploadToken).catch(() =>
              Toast.error({ message: "The document was uploaded, but could not be attached" })
            );
          }}
        />
      )}
      {/*
        The same modal, given the group and no project, uploads against the landholding itself: passing
        groupUuid fixes it to a group document, which is the only kind the link accepts for this parent.
      */}
      {showUploadModal && attachmentParentUuid && attachmentParentType === "landholding" && (
        <UploadDocumentModal
          show={showUploadModal}
          closeModal={() => setShowUploadModal(false)}
          groupUuid={attachmentParentUuid}
          projectUuid={null}
          refreshDocuments={refreshUploadedDocuments}
          setRefreshDocuments={setRefreshUploadedDocuments}
          onUploaded={(documents) => {
            handleDocumentsUploaded(documents, uploadToken).catch(() =>
              Toast.error({ message: "The document was uploaded, but could not be attached" })
            );
          }}
        />
      )}
      <Row spacingV="xl">
        <Column span={12}>
          {hasFullEditAccess ? (
            <TextArea
              name="Description"
              label="Description"
              value={currentTask?.description ?? ""}
              displayCharacterCount
              maxLength={500}
              onChange={(value) => setCurrentTask({ ...currentTask, description: value })}
            />
          ) : (
            <>
              <FormLabel label="Description" />
              <p className="body1">{currentTask?.description ?? ""}</p>
            </>
          )}
        </Column>
      </Row>
      {!isCreateTask && (
        <Row spacingV="xl">
          <Column span={12}>
            <DiscussionsPanel
              threads={threads}
              permissions={permissions}
              currentUserUuid={currentUserUuid}
              variant={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "seed" : "hub"}
              font={
                currentUserType === OrganisationTypeConstants.ASSET_MANAGER ||
                currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER
                  ? "figtree"
                  : "space-grotesk"
              }
              hasMoreFromServer={hasMoreFromServer}
              hasMoreResolvedFromServer={hasMoreResolvedFromServer}
              onShowResolvedChange={onShowResolvedChange}
              onShowMore={onShowMore}
              onShowMoreResolved={onShowMoreResolved}
              onAddComment={onAddComment}
              onReply={onReply}
              onEditThreadMessage={onEditThreadMessage}
              onEditReply={onEditReply}
              onDeleteThread={onDeleteThread}
              onDeleteReply={onDeleteReply}
              onResolveThread={onResolveThread}
              errors={discussionErrors}
              headerSize="small"
            />
          </Column>
        </Row>
      )}
      <Row spacingV="xl">
        <Column span={12}>
          <Divider type="horizontal" />
        </Column>
      </Row>
      {hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.User, workflowOrganisationAccessLevel) && (
        <Row>
          <Column span={12}>
            <Button text="Save" onClick={handleSubmit} hasNoPadding isLoading={isHandleSubmitLoading} />
            <Button text="Cancel" variant="secondary" onClick={handleCloseTaskPanel} hasNoPadding />
          </Column>
        </Row>
      )}
    </div>
  );
};
